import type { ICommandBarItemProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { useAIActionsCommandBarItem } from 'components/Buttons/useAIActionsCommandBarItem';
import { AIActionBinding, createAIActionBinding } from 'models/copilot/actions/aiActionBinding';
import { askAssistantTemplate, sceneHelpTemplate } from 'models/copilot/actions/aiActionTemplates';
import { useContext, useMemo } from 'react';
import { isCopilotPanelAvailable } from './copilotPanelAvailability';
import { useCopilotAssistantBindings } from './useCopilotAssistantBindings';

const noSceneBindings: AIActionBinding[] = [];

/**
 * A user scene's AI command bar item: the scene's own bindings, the active usage's prompts and processes
 * for this page (worded on the selected rows when a prompt takes them), then Ask AI last when the usage
 * offers no help question: on a usage answered by its own agent, and while the chat is off (no prompt is
 * listed then). Undefined when the panel is not available to this user. A scene that passes its own
 * bindings memoizes them.
 */
export const useSceneAIActionsItem = (
  selectedEntityIds?: number[],
  sceneBindings: AIActionBinding[] = noSceneBindings,
): ICommandBarItemProps | undefined => {
  const appContext = useContext(AppContext);
  const assistantBindings = useCopilotAssistantBindings(selectedEntityIds);
  const panelAvailable = isCopilotPanelAvailable(appContext);

  const bindings = useMemo(() => {
    if (!panelAvailable) return [];

    const listed = [...sceneBindings, ...assistantBindings];
    const offersHelp = listed.some((binding) => binding.template === sceneHelpTemplate);

    return offersHelp ? listed : [...listed, createAIActionBinding(askAssistantTemplate, undefined)];
  }, [panelAvailable, assistantBindings, sceneBindings]);

  return useAIActionsCommandBarItem(bindings);
};
